/**
 * Base error class for all Hikvision API related errors
 */
export abstract class HikvisionError extends Error {
  abstract readonly code: string;
  
  constructor(message: string, cause?: Error) {
    super(message);
    this.name = this.constructor.name;
    this._cause = cause;
    
    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  private readonly _cause?: Error;

  /**
   * Returns a JSON representation of the error
   */
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      stack: this.stack,
      cause: this._cause?.message,
    };
  }
}

/**
 * Error thrown when connection to Hikvision device fails
 */
export class ConnectionError extends HikvisionError {
  readonly code = 'CONNECTION_ERROR';
  
  constructor(message: string, cause?: Error) {
    super(`Connection failed: ${message}`, cause);
  }
}

/**
 * Error thrown when authentication with Hikvision device fails
 */
export class AuthenticationError extends HikvisionError {
  readonly code = 'AUTHENTICATION_ERROR';
  
  constructor(message: string, cause?: Error) {
    super(`Authentication failed: ${message}`, cause);
  }
}

/**
 * Error thrown when configuration validation fails
 */
export class ValidationError extends HikvisionError {
  readonly code = 'VALIDATION_ERROR';
  
  constructor(message: string, cause?: Error) {
    super(`Validation failed: ${message}`, cause);
  }
}

/**
 * Error thrown when API request fails
 */
export class ApiError extends HikvisionError {
  readonly code = 'API_ERROR';
  
  constructor(
    message: string,
    // eslint-disable-next-line no-unused-vars
    public readonly _statusCode?: number,
    // eslint-disable-next-line no-unused-vars
    public readonly _responseData?: unknown,
    cause?: Error
  ) {
    super(`API request failed: ${message}`, cause);
  }
}

/**
 * Error thrown when device operation times out
 */
export class TimeoutError extends HikvisionError {
  readonly code = 'TIMEOUT_ERROR';
  
  constructor(message: string, public readonly timeoutMs: number, cause?: Error) {
    super(`Operation timed out after ${timeoutMs}ms: ${message}`, cause);
  }
}

/**
 * Error thrown when device is not found or unreachable
 */
export class DeviceNotFoundError extends HikvisionError {
  readonly code = 'DEVICE_NOT_FOUND';
  
  constructor(deviceIdentifier: string, cause?: Error) {
    super(`Device not found: ${deviceIdentifier}`, cause);
  }
}